/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { cn } from '@/lib/utils';

/**
 * ProductCarouselSkeleton component provides a loading state placeholder for product carousels.
 *
 * This skeleton component mimics the layout of a product carousel including:
 * - Optional title skeleton
 * - Horizontal carousel layout with multiple product card skeletons
 * - Navigation controls skeleton
 *
 * Used to improve perceived performance while product data is being fetched
 * from the commerce API, providing visual feedback to users during loading states.
 *
 * @param props - The component props
 * @param props.title - Optional title to display above the carousel skeleton
 * @param props.itemCount - Number of skeleton items to display (default: from config)
 * @param props.titleClassName - Mirrors `ProductCarousel`'s prop of the same name, so the
 *   skeleton reserves space for the title the real carousel will actually draw. Passing
 *   `sr-only` (the PDP region's pattern — one region heading, accessible-name-only carousel
 *   titles) collapses the title placeholder instead of reserving a bar for a title that never
 *   becomes visible.
 *
 * @returns JSX element representing the product carousel skeleton layout
 *
 * @example
 * ```tsx
 * // Basic usage with default item count
 * <ProductCarouselSkeleton title="Featured Products" />
 *
 * // Usage with custom item count
 * <ProductCarouselSkeleton title="Recommended" itemCount={6} />
 *
 * // Usage without title
 * <ProductCarouselSkeleton />
 *
 * // Title is an accessible name only — reserve no visible bar for it
 * <ProductCarouselSkeleton title="Recently Viewed" titleClassName="sr-only" />
 * ```
 *
 * @since 1.0.0
 */
export default function ProductCarouselSkeleton({
    title,
    itemCount,
    className,
    titleClassName,
}: {
    title?: string;
    itemCount?: number;
    className?: string;
    titleClassName?: string;
}) {
    const config = useConfig();
    const finalItemCount = itemCount ?? config.global.carousel.defaultItemCount;
    return (
        <div className={cn('w-full section-container py-6 animate-pulse', className)}>
            {title && (
                <div className={cn('flex items-center justify-between', titleClassName)}>
                    <Skeleton className="h-8 md:h-9 w-64" />
                </div>
            )}

            <div className="py-6">
                <div className="relative w-full">
                    {/* CarouselContent outer wrapper with overflow-hidden */}
                    <div className="overflow-hidden">
                        {/* CarouselContent inner flex container - includes -ml-4 to match scrollable carousel */}
                        <div className="flex -ml-4 items-stretch flex-nowrap">
                            {/* THE SLIDE WIDTH MUST TRACK THE REAL RAIL'S, OR THE FALLBACK
                                RESERVES THE WRONG BOX. These three arms are the same token
                                and the same per-breakpoint fallbacks the real item carries in
                                `carousel.tsx` — read that file's leading comment for why the
                                value is a count rather than a pixel, and why the fallbacks
                                restate the literals this file used to hardcode.

                                Restating them is what makes this a no-op: a surface whose
                                ancestors never declare `--rail-slide-width` computes exactly
                                348 / 256 / 288 as before. Only a surface that opts in moves,
                                and then the skeleton moves WITH it.

                                It has to. The tile art is `aspect-[var(--tile-art-aspect)]`,
                                so its height is a function of the slide width — a skeleton
                                pinned at 348 in front of a rail that resolves 128 would
                                reserve roughly 275px of height the real rail never uses, and
                                collapse it on hydration. A fallback that reserves the wrong
                                height is CLS wearing a skeleton. */}
                            {Array.from({ length: finalItemCount }, (_, i) => i).map((index) => (
                                <div
                                    key={`carousel-item-${index}`}
                                    className="w-[var(--rail-slide-width,348px)] md:w-[var(--rail-slide-width,256px)] 2xl:w-[var(--rail-slide-width,288px)] basis-auto py-1 flex">
                                    <div className="w-full max-w-full min-w-0 flex">
                                        <ProductTileSkeleton />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Navigation controls skeleton */}
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2">
                        <Skeleton className="h-9 w-9" />
                    </div>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2">
                        <Skeleton className="h-9 w-9" />
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Individual product tile skeleton component for use within the carousel.
 *
 * This component creates a skeleton placeholder that matches the structure
 * of the actual ProductTile component, including image, swatches, title, and price areas.
 *
 * @returns JSX element representing a single product tile skeleton
 */
function ProductTileSkeleton() {
    return (
        <Card className="border-0 overflow-hidden w-full max-w-full flex flex-col h-full gap-0 py-0">
            {/* Image area.

                THE SHAPE IS READ FROM THE SAME TOKEN THE REAL TILE READS, and it has
                to be: this box is what reserves space before the art arrives, so if it
                and `product-image/index.tsx` disagree the rail reflows the moment the
                products land. It used to be an inline `aspect-ratio: 0.8 / 1` — a second,
                independent copy of NM's `380 / 475` — which reserved an NM-shaped box on
                BG, whose `--tile-art-aspect` is `5 / 7`. Same class, same custom property,
                so the two cannot drift apart again. */}
            <CardHeader className="p-0">
                <Skeleton className="w-full aspect-[var(--tile-art-aspect)]" />
            </CardHeader>

            {/* NO SWATCH PLACEHOLDER, because the tile this stands in for no longer
                renders a swatch row. `carousel.tsx` passes `maxSwatches={0}` to every
                ProductTile it mounts — live's rail tile has four children and none of
                them is a swatch row, measured container-bounded on the raw dumps for
                the NM homepage rail, the NM cart rail and the BG PDP rail; the citation
                lives at the call site.

                A skeleton that reserves a row the real tile will not paint is not a
                cosmetic mismatch — it is the CLS this component exists to prevent, in
                reverse: two 28px discs plus their pt-3 come out of the rail's height at
                the moment the products arrive. Same reasoning as the art box below,
                which reads `--tile-art-aspect` rather than carrying its own copy of the
                shape, so the two cannot drift. */}

            {/* Product name */}
            <CardContent className="px-4 pt-2 pb-0">
                <Skeleton className="h-4 w-full mb-1" />
                <Skeleton className="h-4 w-3/4" />
            </CardContent>

            {/* Price */}
            <CardContent className="px-4 pt-2 pb-4">
                <Skeleton className="h-5 w-16" />
            </CardContent>
        </Card>
    );
}
